import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { useAllSchools, useLessonById } from '@/lib/api-call-hooks';
import { EmptyArray, getAdminSessions, getUser } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { useErrorToast } from '@/lib/error-handling';
import { usePaginatedFilters } from '@/lib/filtering';
import { paginationRequestSchema } from '@/lib/pagination';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { distinct, FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import {
  AdminSession,
  classTimeTypeSchema,
  lessonKindSchema,
  LessonKindToLabel,
  studentYearSchema,
} from '@airreading/common/data';
import { pluralize } from '@/lib/string-utils';
import { coachesTabs } from '../../-components/tabs';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { validateRouteRole } from '@/lib/routing';
import { getStudentYearOptions } from '@/lib/student-utils';
import { buildNameLikeSelectOptions } from '@/components/ui/multi-select-utils';

export const Route = createFileRoute('/_layout/teachers/$teacherId/future-sessions/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: TeacherFutureSessionsIndex,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    lessons: z.string().array().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    lessonKinds: z.array(lessonKindSchema).optional(),
    schoolIds: z.string().array().optional(),
    orgIds: z.string().array().optional(),
    grades: z.array(studentYearSchema).optional(),
  }),
});

const nowInCST = Temporal.Now.zonedDateTimeISO(FrequentTimeZones.UsCentral);
const today = nowInCST.startOfDay().toInstant().toString();

function TeacherFutureSessionsIndex() {
  const queryClient = useQueryClient();
  const { teacherId } = Route.useParams();

  const { data: user } = useQuery({
    queryKey: ['user', teacherId],
    queryFn: () => getUser(teacherId),
  });
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      lessons: [[selectedLessons, activeSelectedLessons], setSelectedLessons],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      startDate: [[selectedStartDate], setSelectedStartDate],
      endDate: [[selectedEndDate], setSelectedEndDate],
      schoolIds: [[schoolIds, activeSchoolIds], setSchoolIds],
      orgIds: [[orgIds, activeOrgIds], setOrgIds],
      grades: [[grades, activeGrades], setGrades],
      lessonKinds: [[selectedLessonKinds, activeSelectedLessonKinds], setSelectedLessonKinds],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'lessons',
    'classTypes',
    'startDate',
    'endDate',
    'lessonKinds',
    'schoolIds',
    'orgIds',
    'grades',
  ]);

  const { data: allSchools, isPending: isLoadingSchools } = useAllSchools();
  const allSchoolOptions = allSchools.map(buildNameLikeSelectOptions);
  const allOrgOptions = allSchools
    .map((school) => school.org)
    .filter(distinct((o) => o.id))
    .map(buildNameLikeSelectOptions);

  const endPlanDate = plainDateFromStringOrElseUndefined(selectedEndDate);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      filters: {
        startOnOrAfter: selectedStartDate
          ? Temporal.PlainDate.from(selectedStartDate)
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .startOfDay()
              .toInstant()
              .toString()
          : today,
        startOnOrBefore: endPlanDate
          ? endPlanDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        query: activeQuery,
        lessons: activeSelectedLessons,
        classTypes: activeSelectedClassTypes,
        coachId: teacherId, // Filter by the current teacher ID
        lessonKinds: activeSelectedLessonKinds,
        grades: activeGrades,
        schools: activeSchoolIds,
        orgs: activeOrgIds,
      },
    }),
  );

  useErrorToast(error, 'Error loading sessions. Please try again.');

  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const fetchLessonById = useLessonById();

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student name or student email"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterDropdown
          title="District"
          options={allOrgOptions}
          selectedValues={orgIds ?? []}
          onChange={setOrgIds}
          isLoading={isLoadingSchools}
        />
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={schoolIds ?? []}
          onChange={setSchoolIds}
          isLoading={isLoadingSchools}
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={grades || []}
          onChange={setGrades}
        />
        <TableFilterDropdown
          title="Lesson type"
          options={lessonKindSchema.options.map((type) => ({
            label: LessonKindToLabel[type],
            value: type,
          }))}
          selectedValues={selectedLessonKinds ?? []}
          onChange={setSelectedLessonKinds}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedStartDate}
          toPlainDateString={selectedEndDate}
          onRangeSelect={(range) => {
            setSelectedStartDate(range.fromPlainDateString);
            setSelectedEndDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      queryClient,
      selectedLessons,
      setSelectedLessons,
      fetchLessonById,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedStartDate,
      selectedEndDate,
      resetFilters,
      setQuery,
      setSelectedStartDate,
      setSelectedEndDate,
    ],
  );

  return (
    <InfoPage
      headerText={user?.fullNameOrEmail || 'Future Sessions'}
      tabs={coachesTabs({ teacherId })}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Teachers',
            link: {
              to: '/teachers',
            },
          },
        ],
        currentPage: user?.fullNameOrEmail || 'Future Sessions',
      }}
    >
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        from="teacher-future"
      />
    </InfoPage>
  );
}
